import { Component, type ReactNode } from 'react'

interface Props {
    /** Renders the widget. Called inside the boundary so a throw in the page's own mapping code is caught too. */
    render: () => ReactNode
    /** A change clears a caught error and renders the widget again — the grids pass their fetch state. */
    resetKey?: unknown
}

interface State {
    error: Error | null
    resetKey: unknown
}

function WidgetContent({ render }: { render: () => ReactNode }) {
    return render()
}

/**
 * Keeps a widget that throws while rendering inside its own grid cell. React 19 unmounts the whole
 * root on an uncaught render error, so without this one widget choking on an unexpected response
 * blanked the entire dashboard.
 */
export default class WidgetErrorBoundary extends Component<Props, State> {
    state: State = { error: null, resetKey: undefined }

    static getDerivedStateFromError(error: Error): Partial<State> {
        return { error }
    }

    static getDerivedStateFromProps(props: Props, state: State): Partial<State> | null {
        return props.resetKey !== state.resetKey ? { resetKey: props.resetKey, error: null } : null
    }

    render() {
        if (!this.state.error) return <WidgetContent render={this.props.render} />
        return (
            <div role="alert" style={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '1rem', color: 'var(--color-text-tertiary)', fontSize: 'var(--text-sm)', textAlign: 'center' }}>
                <span>This widget couldn't be displayed.</span>
                <button className="btn btn-secondary btn-sm" onClick={() => this.setState({ error: null })}>
                    Try again
                </button>
            </div>
        )
    }
}
